import CustomCheckBox from "../customCheckBox/customCheckBox";

interface CheckboxListProps<T> {
  items: T[];
  selectedItems: T[] | string[];
  onToggle: (item: T) => void;
  getLabel: (item: T) => string;
  getKey: (item: T) => string;
  emptyMessage: string;
  idPrefix: string;
  isItemSelected?: (item: T, selectedItems: T[] | string[]) => boolean;
  disabled?: boolean;
}

const defaultIsItemSelected = <T,>(
  item: T,
  selected: T[] | string[],
  getKey: (item: T) => string,
): boolean => {
  const itemKey = getKey(item);
  return selected.some(
    (selectedItem) =>
      (typeof selectedItem === "string"
        ? selectedItem
        : getKey(selectedItem)) === itemKey,
  );
};

const CheckboxList = <T,>({
  items,
  selectedItems,
  onToggle,
  getLabel,
  getKey,
  emptyMessage,
  idPrefix,
  isItemSelected,
  disabled,
}: CheckboxListProps<T>) => {
  const checkIsSelected =
    isItemSelected ||
    ((item: T, selected: T[] | string[]) =>
      defaultIsItemSelected(item, selected, getKey));

  if (items.length === 0) {
    return (
      <div className="px-4 py-3 text-sm leading-[1.375rem] text-[#6B7280]">
        {emptyMessage}
      </div>
    );
  }

  return (
    <>
      {items.map((item) => {
        const key = getKey(item);
        const label = getLabel(item);
        const isChecked = checkIsSelected(item, selectedItems);

        return (
          <div
            key={key}
            className="flex items-center border-b border-[#e5e7eb] px-4 py-3 transition-colors duration-200 last:border-b-0 hover:bg-[#f9fafb]"
          >
            <CustomCheckBox
              id={`${idPrefix}-${key}`}
              label={label}
              isChecked={isChecked}
              onCheckBoxValueChange={() => onToggle(item)}
              className="flex-1"
              isDisabled={disabled}
            />
          </div>
        );
      })}
    </>
  );
};

export default CheckboxList;
